import {Award, BadgeCheck, CheckCircle, Clock, Download, ExternalLink, ListIcon, OctagonX, QrCode} from 'lucide-react';

import React, {useEffect, useState} from 'react';
import * as QRCodeLib from 'qrcode.react';
import {Link} from "react-router-dom";
import {sprintf} from "@/lib/utils/sprintf";
import {URLS} from "@/lib/urls";
import {Button, Spin} from "antd";
import RenderIf from "@/components/render-if";
import ApiHandleSuccessMessage, {ApiHandleErrorMessage} from "@/lib/helper/apiHandleResultMessage";
import {useApiCreateCertificationPDF} from "@/api/certification";

export default function CertificateCard({certificate, refetch}) {

    const [lockStudentData, setLockStudentData] = useState(false)
    const [showQR, setShowQR] = useState(false);
    const hasSurvey = certificate.survey_detail !== undefined;
    const {mutate: pdfRequest, isPending: isPendingPDFRequest} = useApiCreateCertificationPDF();

    const canSee = ((certificate?.survey_detail?.date && certificate?.survey_detail?.question_count === 0) || certificate.survey_date)

    const handleDownload = () => {
        pdfRequest({id: certificate?.id}, {
            onSuccess: (data) => {
                ApiHandleSuccessMessage(data)
                refetch()
            }, onError: (error) => {
                ApiHandleErrorMessage(error)
            }
        })

    };

    useEffect(() => {
        if (certificate?.useracademy) {
            setLockStudentData(!certificate?.useracademy?.lock)
        }
    }, [certificate])

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('fa-IR');
    };

    const getPdfStatusBadge = (status) => {
        switch (status) {
            case 'done':
                return (<span
                    className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircle className="w-3.5 h-3.5"/>
            آماده دانلود
          </span>);
            case 'queue':
                return (<span
                    className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
            <Clock className="w-3.5 h-3.5"/>
            در حال آماده‌سازی
          </span>);

            case 'failed':
                return (<span
                    className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <OctagonX className="w-3.5 h-3.5"/>
            خطا در هنگام تولید تصویر
          </span>);

            default:
                return null;
        }
    };


    return (<>
        <div className="group relative">
            <div
                className="absolute inset-0  rounded-2xl "/>

            <div
                className="relative  rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 p-5 border border-gray-100 dark:border-gray-600 hover:border-blue-200 overflow-hidden">


                <div className="relative flex items-start gap-4">
                    <div className="flex-shrink-0 relative">
                        <div
                            className={`absolute inset-0 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl blur opacity-40 group-hover:opacity-60 transition-opacity duration-300`}/>
                        <img
                            src={certificate.certification.setting.background}
                            alt={certificate.certification.name}
                            className="relative w-28 h-28 rounded-xl object-cover shadow-lg ring-2 ring-white"
                        />
                        <div
                            className="absolute -bottom-2 -right-2 bg-gradient-to-br from-blue-600 to-cyan-600 text-white px-2 py-1 rounded-lg shadow-md flex items-center gap-1 text-xs font-bold">
                            <Award className="w-3 h-3"/>
                            {certificate.score}
                        </div>
                    </div>

                    <div className="flex-1 min-w-0 pt-1">
                        <div className="flex items-start gap-2 mb-2">

                            <BadgeCheck className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5"/>
                            <h3 className="text-base font-bold  line-clamp-2">
                                {certificate.certification.name}
                            </h3>
                        </div>

                        <div className="space-y-2 mb-4">
                            <p className="text-xs  flex items-center gap-2">
                                <span className="inline-block w-1 h-1 bg-gray-400 rounded-full"/>
                                امتیاز
                                <span className={'mx-2 font-bold text-blue-600'}>{certificate.score}</span>

                            </p>
                            <p className="text-xs  flex items-center gap-2">
                                <span className="inline-block w-1 h-1 bg-gray-400 rounded-full"/>
                                تاریخ دریافت
                                <span className={'mx-2'}>{formatDate(certificate.datecreated)}</span>

                            </p>

                            <RenderIf condition={lockStudentData}>
                                <div
                                    className="flex items-center gap-1.5 text-xs text-red-600 bg-red-50 px-2.5 py-1.5 rounded-lg w-fit">
                                    <ListIcon className="w-3.5 h-3.5"/>
                                    <span>اطلاعات هویتی شما توسط مدیر سیستم تایید نشده است. برای نمایش گواهی نامه ابتدا باید اطلاعات هویتی تایید شود</span>
                                </div>
                            </RenderIf>

                            {hasSurvey && (<div
                                className="flex items-center gap-1.5 text-xs text-green-600 bg-green-50 px-2.5 py-1.5 rounded-lg w-fit">
                                <CheckCircle className="w-3.5 h-3.5"/>
                                <span>نظرسنجی تکمیل شده</span>
                            </div>)}

                            <div>
                                {getPdfStatusBadge(certificate.pdf_status)}
                            </div>
                        </div>

                        <div className="flex items-center gap-2 flex-wrap">

                            <RenderIf condition={canSee} falsyElement={<Link
                                to={sprintf(URLS.student.survey.certificate, {id: certificate.id})}>
                                <p className={'text-sm my-1'}> جهت دریافت گواهی‌نامه لطفا ابتدا در نظرسنجی شرکت
                                    کنید.</p>

                                <Button type="primary" size="middle"
                                        className={'bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600'}>شرکت
                                    در نظرسنجی</Button>
                            </Link>}>
                                <a href={certificate.url}
                                   target="_blank"
                                   rel="noopener noreferrer"

                                >
                                    <button
                                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 transform hover:scale-105  shadow-sm hover:shadow-md bg-gradient-to-r from-blue-600 to-cyan-600 text-white hover:from-blue-700 hover:to-cyan-700`}
                                    >
                                        <ExternalLink className="w-4 h-4"/>
                                        مشاهده گواهینامه
                                    </button>
                                </a>

                                <RenderIf
                                    condition={!lockStudentData && (certificate?.pdf_status === 'queue' || !certificate?.pdf_status)}>
                                    <button
                                        onClick={handleDownload}
                                        disabled={certificate?.pdf_status === 'queue'}

                                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 transform hover:scale-105  shadow-sm hover:shadow-md   `}
                                    >
                                        <RenderIf condition={isPendingPDFRequest}>
                                            <Spin spinning={true} size={'small'}/>
                                        </RenderIf>


                                        {certificate?.pdf_status === 'queue' ? (<>
                                            <Clock className="w-4 h-4"/>
                                            <span>در حال آماده سازی</span>
                                        </>) : (<>
                                            <Download className="w-4 h-4"/>
                                            دانلود
                                        </>)}

                                    </button>
                                </RenderIf>
                                <RenderIf condition={certificate?.pdf_status === 'done' && certificate?.pdf_file_path}>
                                    <Link to={certificate?.pdf_file_path} target={'_blank'}
                                          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 transform   shadow-sm hover:shadow-md   `}
                                          download={true}
                                    >
                                        <Download className="w-4 h-4"/>
                                        دریافت فایل گواهی‌نامه
                                    </Link>
                                </RenderIf>


                            </RenderIf>
                            <RenderIf condition={canSee && !lockStudentData}>
                                <button

                                    onClick={() => setShowQR(!showQR)}
                                    className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 transform`}
                                >
                                    <span>QR</span>
                                    <QrCode className="w-4 h-4"/>
                                </button>
                            </RenderIf>
                        </div>
                    </div>
                </div>

                {showQR && (<div
                    className="mt-5 pt-5 border-t border-gray-100 flex justify-center animate-in fade-in slide-in-from-bottom-2 duration-300">
                    برای مشاهده نسخه دیجیتالی گواهی‌نامه، این کد را اسکن کنید یا دکمه مشاهده گواهی‌نامه را بزنید
                    <div
                        className=" p-4 rounded-xl border border-gray-100 dark:border-gray-600 shadow-sm">


                        <QRCodeLib.QRCodeSVG value={certificate.url} size={140} level="H" includeMargin={true}/>
                    </div>
                </div>)}
            </div>
        </div>
    </>);
}
